// Document review: the claim PDF on the page; anyone with access clicks a spot to leave a numbered comment (SWI, the
// architect, client finance — all visible at once, unless SWI only). Then one annotated PDF + comment register goes to PBS.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, apiBlob, viewUrl, write } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import { useLive } from '../../lib/live';
import { fmtDateTime } from '../../lib/format';
import type { FileRow } from '../ItemFiles';
import { Button, errText, useToast } from '../kit';

type PinRow = {
  id: string;
  parent_id: string | null;
  body: string;
  author_name: string | null;
  posted_at: string;
  visibility: 'internal' | 'shared';
  anchor: { file_id: string; page: number; x: number; y: number } | null;
  for_contractor: boolean;
  resolved_at: string | null;
  deleted_at: string | null;
};
type PdfDoc = { numPages: number; getPage: (n: number) => Promise<PdfPage>; destroy: () => Promise<void> };
type PdfPage = {
  getViewport: (o: { scale: number }) => { width: number; height: number };
  render: (o: { canvasContext: CanvasRenderingContext2D; viewport: { width: number; height: number }; canvas?: HTMLCanvasElement }) => { promise: Promise<void> };
};

let pdfjsLoad: Promise<{ getDocument: (o: { url: string }) => { promise: Promise<PdfDoc> } }> | null = null;
async function pdfjs() {
  pdfjsLoad ??= (async () => {
    const lib = await import('pdfjs-dist');
    const worker = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
    lib.GlobalWorkerOptions.workerSrc = worker;
    return lib as unknown as { getDocument: (o: { url: string }) => { promise: Promise<PdfDoc> } };
  })();
  return pdfjsLoad;
}

function Page({ doc, n, width, pins, active, adding, onAdd, onPick }: { doc: PdfDoc; n: number; width: number; pins: { n: number; row: PinRow }[]; active: string | null; adding: boolean; onAdd: (page: number, x: number, y: number) => void; onPick: (id: string) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [h, setH] = useState(width * 1.414);
  useEffect(() => {
    let off = false;
    void (async () => {
      const page = await doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const scale = width / base.width;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const vp = page.getViewport({ scale: scale * dpr });
      const c = ref.current;
      if (!c || off) return;
      c.width = vp.width;
      c.height = vp.height;
      setH(base.height * scale);
      await page.render({ canvasContext: c.getContext('2d')!, viewport: vp, canvas: c }).promise;
    })();
    return () => {
      off = true;
    };
  }, [doc, n, width]);
  return (
    <div
      className={`rv-page ${adding ? 'adding' : ''}`}
      style={{ width, height: h }}
      data-page={n}
      onClick={(e) => {
        if (!adding) return;
        const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        onAdd(n, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
      }}
    >
      <canvas ref={ref} style={{ width, height: h }} aria-label={`Page ${n}`} />
      {pins.map((p) => (
        <button
          key={p.row.id}
          type="button"
          className={`rv-pin ${p.row.resolved_at ? 'res' : ''} ${p.row.visibility === 'internal' ? 'int' : ''} ${active === p.row.id ? 'on' : ''}`}
          style={{ left: `${p.row.anchor!.x * 100}%`, top: `${p.row.anchor!.y * 100}%` }}
          onClick={(e) => (e.stopPropagation(), onPick(p.row.id))}
          aria-label={`Comment ${p.n}`}
        >
          {p.n}
        </button>
      ))}
      <span className="rv-num">{n}</span>
    </div>
  );
}

export function Review({ itemId, files, canComment, canSend, internal }: { itemId: string; files: FileRow[]; canComment: boolean; canSend: boolean; internal: boolean }) {
  const toast = useToast();
  const pdfs = files.filter((f) => f.is_current && f.kind === 'file' && /pdf/i.test(f.mime ?? '') && !f.comment_id);
  const [fileId, setFileId] = useState('');
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<PinRow[]>([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ page: number; x: number; y: number; text: string; swi: boolean; pbs: boolean } | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'res' | 'pbs'>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);

  useEffect(() => {
    if (!fileId && pdfs.length) setFileId(pdfs[0].id);
  }, [pdfs, fileId]);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.max(260, Math.min(900, el.clientWidth - 4))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (!fileId) return;
    let off = false;
    let loaded: PdfDoc | null = null;
    setDoc(null);
    setErr(null);
    void (async () => {
      try {
        const [lib, v] = await Promise.all([pdfjs(), viewUrl(fileId)]);
        loaded = await lib.getDocument({ url: v.url }).promise;
        if (!off) setDoc(loaded);
      } catch (e) {
        if (!off) setErr(errText(e));
      }
    })();
    return () => {
      off = true;
      void loaded?.destroy();
    };
  }, [fileId]);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('comments')
      .select('id, parent_id, body, author_name, posted_at, visibility, anchor, for_contractor, resolved_at, deleted_at')
      .eq('linked_table', 'items')
      .eq('linked_id', itemId)
      .is('deleted_at', null)
      .order('posted_at');
    setRows((data ?? []) as PinRow[]);
  }, [itemId]);
  useEffect(() => void load(), [load]);
  useLive(['comments'], () => void load(), { filter: `linked_id=eq.${itemId}` });

  const pins = useMemo(
    () =>
      rows
        .filter((r) => !r.parent_id && r.anchor?.file_id === fileId)
        .sort((a, b) => a.anchor!.page - b.anchor!.page || a.posted_at.localeCompare(b.posted_at))
        .map((row, i) => ({ n: i + 1, row })),
    [rows, fileId],
  );
  const shown = pins.filter((p) => (filter === 'open' ? !p.row.resolved_at : filter === 'res' ? p.row.resolved_at : filter === 'pbs' ? p.row.for_contractor : true));
  const replies = (id: string) => rows.filter((r) => r.parent_id === id);

  async function save() {
    if (!draft || !draft.text.trim()) return;
    try {
      await write('comments', 'insert', {
        values: { linked_id: itemId, body: draft.text.trim(), visibility: draft.swi ? 'internal' : 'shared', anchor: { file_id: fileId, page: draft.page, x: draft.x, y: draft.y }, for_contractor: draft.pbs, mentions: [] },
        summary: `Review comment on page ${draft.page}`,
      });
      setDraft(null);
      setAdding(false);
      void load();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function flag(r: PinRow, v: { resolved?: boolean; for_contractor?: boolean }) {
    try {
      await api('claim-action', { action: 'pin', comment_id: r.id, ...v });
      void load();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function exportPdf(forPbs: boolean) {
    setBusy('pdf');
    try {
      const { url } = await apiBlob(`review-pdf?item_id=${encodeURIComponent(itemId)}&file_id=${encodeURIComponent(fileId)}${forPbs ? '&for=pbs' : ''}`);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }
  async function sendPbs() {
    const n = pins.filter((p) => p.row.visibility === 'shared' && !p.row.resolved_at).length;
    const note = window.prompt(`Email PBS the claim with the open shared comments (${n}) and a comment register. Note (optional):`, '');
    if (note === null) return;
    setBusy('send');
    try {
      await api('claim-action', { action: 'send_pbs', item_id: itemId, file_id: fileId, note });
      toast({ tone: 'ok', text: 'Sent to PBS' });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }
  const pick = (id: string) => {
    setActive(id);
    const p = pins.find((x) => x.row.id === id);
    wrap.current?.querySelector(`[data-page="${p?.row.anchor?.page}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    document.getElementById(`pin-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  if (!pdfs.length) return <p className="muted small">Upload the claim PDF to Files to review it here.</p>;
  const open = pins.filter((p) => !p.row.resolved_at).length;
  return (
    <div className="rv">
      <div className="resp-row">
        <select value={fileId} onChange={(e) => setFileId(e.target.value)} aria-label="Document">
          {pdfs.map((f) => (
            <option key={f.id} value={f.id}>
              {f.original_name}
            </option>
          ))}
        </select>
        {canComment ? (
          <Button kind={adding ? 'primary' : 'secondary'} aria-pressed={adding} onClick={() => (setAdding(!adding), setDraft(null))}>
            {adding ? 'Click a spot on the page…' : '+ Comment on the page'}
          </Button>
        ) : null}
        <span className="grow" />
        <span className="muted small">
          {open} open · {pins.length - open} resolved
        </span>
        <Button kind="ghost" onClick={() => void exportPdf(false)} disabled={Boolean(busy)}>
          {busy === 'pdf' ? 'Making…' : 'Annotated PDF'}
        </Button>
        {canSend ? (
          <Button onClick={() => void sendPbs()} disabled={Boolean(busy) || !open}>
            Send to PBS for response
          </Button>
        ) : null}
      </div>
      <div className="rv-grid">
        <div className="rv-pages" ref={wrap}>
          {err ? <div className="error-box">Couldn’t show the PDF: {err}</div> : null}
          {!doc && !err ? <div className="empty">Loading the PDF…</div> : null}
          {doc
            ? Array.from({ length: doc.numPages }, (_, i) => i + 1).map((n) => (
                <Page
                  key={`${fileId}-${n}`}
                  doc={doc}
                  n={n}
                  width={width}
                  pins={pins.filter((p) => p.row.anchor!.page === n)}
                  active={active}
                  adding={adding}
                  onPick={pick}
                  onAdd={(page, x, y) => setDraft({ page, x, y, text: '', swi: false, pbs: true })}
                />
              ))
            : null}
        </div>
        <div className="rv-list">
          <div className="chips" role="radiogroup" aria-label="Show">
            {(
              [
                ['all', 'All'],
                ['open', 'Open'],
                ['res', 'Resolved'],
                ['pbs', 'For PBS'],
              ] as const
            ).map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={filter === k} className={`chip ${filter === k ? 'on' : ''}`} onClick={() => setFilter(k)}>
                {l}
              </button>
            ))}
          </div>
          {draft ? (
            <div className="rv-draft">
              <strong className="small">New comment · page {draft.page}</strong>
              <textarea autoFocus rows={3} value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} aria-label="Review comment" placeholder="What needs checking or changing here?" />
              <div className="resp-row">
                {internal ? (
                  <label className="check small">
                    <input type="checkbox" checked={draft.swi} onChange={(e) => setDraft({ ...draft, swi: e.target.checked, pbs: e.target.checked ? false : draft.pbs })} /> <span className="lock">SWI only</span>
                  </label>
                ) : null}
                <label className="check small">
                  <input type="checkbox" checked={draft.pbs} disabled={draft.swi} onChange={(e) => setDraft({ ...draft, pbs: e.target.checked })} /> For PBS response
                </label>
              </div>
              <div className="resp-row">
                <Button kind="primary" onClick={() => void save()} disabled={!draft.text.trim()}>
                  Save
                </Button>
                <Button onClick={() => setDraft(null)}>Cancel</Button>
              </div>
            </div>
          ) : null}
          {shown.length === 0 && !draft ? <p className="muted small">{pins.length ? 'Nothing in this filter.' : 'No comments on this document yet.'}</p> : null}
          {shown.map((p) => (
            <div key={p.row.id} id={`pin-${p.row.id}`} className={`rv-item ${active === p.row.id ? 'on' : ''} ${p.row.resolved_at ? 'res' : ''}`} onClick={() => pick(p.row.id)}>
              <span className={`rv-pin static ${p.row.resolved_at ? 'res' : ''} ${p.row.visibility === 'internal' ? 'int' : ''}`}>{p.n}</span>
              <div className="fu-main">
                <div className="small">
                  <strong>{p.row.author_name ?? 'Someone'}</strong> <span className="muted">· p.{p.row.anchor!.page} · {fmtDateTime(p.row.posted_at)}</span>{' '}
                  {p.row.visibility === 'internal' ? <span className="lock">SWI only</span> : null} {p.row.for_contractor ? <span className="chip-s">For PBS</span> : null}
                </div>
                <div className="cmt-text">{p.row.body}</div>
                {replies(p.row.id).map((r) => (
                  <div key={r.id} className="small muted">
                    ↳ {r.author_name}: {r.body}
                  </div>
                ))}
                {canComment ? (
                  <div className="cmt-actions" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="linkbtn" onClick={() => void flag(p.row, { resolved: !p.row.resolved_at })}>
                      {p.row.resolved_at ? 'Reopen' : 'Resolve'}
                    </button>
                    {p.row.visibility === 'shared' ? (
                      <button type="button" className="linkbtn" onClick={() => void flag(p.row, { for_contractor: !p.row.for_contractor })}>
                        {p.row.for_contractor ? 'Not for PBS' : 'For PBS'}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="muted small">Everyone who can see this claim sees these comments (SWI only ones are hidden from others). Replies go in the comments on the right. “Send to PBS” emails the claim with the open shared comments numbered, plus a comment register for their answers.</p>
    </div>
  );
}
